import { useEffect, useId, useRef, useState } from 'react';
import { newIdempotencyKey } from '../api/client';
import type { DraftReview, JsonObject, Problem, Publication, StudioTrial } from '../api/types';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { ProblemNotice } from '../components/ProblemNotice';
import { usePlatform } from '../app/platform';
import { publishBlockedText } from '../app/status';
import { toProblem } from '../app/useAsync';
import { formatDateTime } from '../lib/datetime';

export interface PublishDialogProps {
  workspaceId: string;
  appId: string;
  appName: string;
  review: DraftReview;
  /**
   * 配備の describe が返した登録簿（build のレポートの declarations、無ければ配備した定義のもの）。
   * どちらも読めなければ null（T16-5）。
   */
  declarations: JsonObject | null;
  onPublished(publication: Publication): void;
  onClose(): void;
}

/** 「公開するもの」の並び。Task の内訳（自動 / AI / ルール / 人）は出さない（G25）。 */
const DECLARATION_LABELS: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'tasks', label: 'Task' },
  { key: 'rpc', label: 'RPC' },
  { key: 'connections', label: '接続先' },
  { key: 'events', label: 'イベント' },
];

/** 見直す点に並べる警告の数。残りは件数だけ出す。 */
const MAX_WARNINGS = 5;

const short = (id: string) => id.slice(0, 8);

/**
 * 登録簿の 1 項目の件数。declarations は JSON のまま届き、出どころで形が違う（T16-5）。
 * build のレポートは「名前の配列」、配備した定義（describe）は「名前 → 定義」のオブジェクトなので
 * どちらも数える。それ以外の読めない値は 0 件とする。
 */
function declarationCount(declarations: JsonObject, key: string): number {
  const value = declarations[key];
  if (Array.isArray(value)) return value.length;
  if (typeof value === 'object' && value !== null) return Object.keys(value).length;
  return 0;
}

/**
 * 検証レポートの節に出す試運転の 1 行。run を始めていなければ出さない。
 * 「1 周済み」は結果が completed のときだけで、失敗・実行中はそのまま言う（A3）。
 */
function trialRunNote(trial: StudioTrial): string | null {
  if (trial.runId === null) return null;
  const run = `run ${short(trial.runId)}`;
  if (trial.outcome === 'completed') return `試運転 1 周済み（${run}）`;
  if (trial.outcome === 'failed') return `試運転が失敗しています（${run}）`;
  if (trial.outcome === 'cancelled') return `試運転を取り消しました（${run}）`;
  return `試運転が実行中です（${run}）`;
}

/**
 * 公開の確認（設計 01 章の段階 6・7.5 の POST A:publish）。
 * 段階でも未設定の必須設定でも止めない（G13・G26）。止めるのは「その版を配備していない」ときだけ。
 */
export function PublishDialog({
  workspaceId,
  appId,
  appName,
  review,
  declarations,
  onPublished,
  onClose,
}: PublishDialogProps) {
  const { api } = usePlatform();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const [note, setNote] = useState('');
  const [running, setRunning] = useState(false);
  const [problem, setProblem] = useState<Problem | null>(null);
  // 通信断の後の再試行が同じ公開として扱われるよう、開いている間ずっと同じ値を使う。
  const [idempotencyKey] = useState(() => newIdempotencyKey());

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog || dialog.open) return;
    dialog.showModal();
  }, []);

  const publication = review.publication;
  // 公開の根拠に送るのは head の digest に一致する検証（validate）のレポートだけ（A6）。
  // kind=trial のレポートの id は DraftReview に無い（残課題）。
  const report = review.report;
  // 出すだけなら前の版のレポートも参考になるので、そうと分かる注記を添えて出す。
  const shown = report ?? review.latestReport;
  const staleReport = report === null && shown !== null;
  const warnings = (shown?.findings ?? []).filter((finding) => finding.severity === 'warning');
  const blocked = publishBlockedText(review);
  const next = (publication?.version ?? 0) + 1;
  const trialNote = review.trial === null ? null : trialRunNote(review.trial);

  const publish = async () => {
    if (running || blocked !== null) return;
    setRunning(true);
    setProblem(null);
    try {
      const created = await api.publishApp(
        workspaceId,
        appId,
        {
          revisionId: review.headRevisionId,
          ...(report ? { reportId: report.id } : {}),
          note,
        },
        { idempotencyKey },
      );
      onPublished(created);
    } catch (error) {
      setProblem(toProblem(error));
      setRunning(false);
    }
  };

  const cancel = () => {
    const dialog = dialogRef.current;
    if (dialog?.open) dialog.close();
    else onClose();
  };

  return (
    <dialog
      ref={dialogRef}
      className="dialog publish-dialog"
      aria-labelledby={titleId}
      onCancel={(event) => {
        if (running) event.preventDefault();
      }}
      onClose={() => {
        if (running) {
          dialogRef.current?.showModal();
          return;
        }
        onClose();
      }}
    >
      <div className="dialog-head">
        <h2 id={titleId} className="dialog-title">{`「${appName}」を公開`}</h2>
      </div>
      <div className="dialog-body">
        <p className="dialog-description">
          {`下書きを 公開中 v${next} にします · ${
            publication === null ? 'まだ公開していません' : `現在の公開は v${publication.version}（${formatDateTime(publication.publishedAt)}）`
          }`}
        </p>

        <section className="publish-section" role="group" aria-label="公開するもの">
          <h3 className="publish-section-title">公開するもの</h3>
          {declarations === null ? (
            <p className="hint">配備した内容から取得できませんでした</p>
          ) : (
            <ul className="publish-counts">
              {DECLARATION_LABELS.map((entry) => (
                <li key={entry.key}>{`${entry.label} ${declarationCount(declarations, entry.key)}`}</li>
              ))}
            </ul>
          )}
        </section>

        <section className="publish-section" role="group" aria-label="検証レポート">
          <h3 className="publish-section-title">検証レポート</h3>
          {shown === null ? (
            <p className="hint">まだ検証していません</p>
          ) : (
            <>
              <p className="row">
                <Badge tone="failed">{`エラー ${shown.errorCount}`}</Badge>
                <Badge tone="unknown">{`警告 ${shown.warningCount}`}</Badge>
                <span className="hint">{`最終検証 ${formatDateTime(shown.createdAt)}`}</span>
              </p>
              {staleReport && <p className="hint">このレポートは現在の版のものではありません</p>}
            </>
          )}
          {trialNote !== null && <p className="hint">{trialNote}</p>}
        </section>

        <section className="publish-section" role="group" aria-label="公開前に見直す点">
          <h3 className="publish-section-title">公開前に見直す点</h3>
          {warnings.length === 0 ? (
            <p className="hint">見直す点はありません</p>
          ) : (
            <>
              <ul>
                {warnings.slice(0, MAX_WARNINGS).map((finding) => (
                  <li key={`${finding.code}:${finding.elementId ?? finding.path ?? ''}`}>{finding.message}</li>
                ))}
              </ul>
              {warnings.length > MAX_WARNINGS && <p className="hint">{`ほか ${warnings.length - MAX_WARNINGS} 件`}</p>}
            </>
          )}
        </section>

        <label className="field">
          <span className="field-label">公開メモ</span>
          <textarea
            className="input"
            aria-label="公開メモ"
            rows={3}
            maxLength={500}
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </label>

        {/* 409 not_deployed は「試す」へ戻す導線がいちばん短い（G26）。 */}
        {problem?.code === 'not_deployed' && (
          <p className="failure" role="alert">
            配備が終わっていません。「試す」で配備してから公開してください。
          </p>
        )}
        <ProblemNotice problem={problem} tone={problem !== null && problem.status === 409 ? 'notice' : 'error'} />
        <p className="hint">公開すると、この Workspace のメンバーが使い始めます。</p>
        {blocked !== null && <p className="publish-blocked">{blocked}</p>}
      </div>
      <div className="dialog-actions">
        <Button disabled={running} onClick={cancel}>
          戻る
        </Button>
        <Button variant="primary" disabled={running || blocked !== null} onClick={() => void publish()}>
          {running ? '公開中…' : '公開する'}
        </Button>
      </div>
    </dialog>
  );
}
